import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  Fade,
  styled,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Avatar,
} from '@mui/material';
import { useFetchNewJoineesQuery } from '../../Home/homeApiSlice';
import { empty } from '../../Gift/assets/index';
import lottie from 'lottie-web';
import NewJoineeLottie from '../../../assets/Announcements/birthday.json';

const NewsJoinees = () => {
  const { data: newJoinees = [], isLoading, isError } = useFetchNewJoineesQuery();
  const [currentIndex, setCurrentIndex] = useState(0);
  const animationContainer = useRef(null);
  const tapTimeoutRef = useRef(null);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [fadeIn, setFadeIn] = useState(true);

  // Restart fade on index change
  useEffect(() => {
    setFadeIn(false);
    const timeout = setTimeout(() => setFadeIn(true), 100);
    return () => clearTimeout(timeout);
  }, [currentIndex]);

  // Load lottie animation
  useEffect(() => {
    if (animationContainer.current) {
      lottie.loadAnimation({
        container: animationContainer.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: NewJoineeLottie,
      });
      animationContainer.current.style.width = '250px';
      animationContainer.current.style.height = '250px';
    }
    return () => {
      if (animationContainer.current) {
        animationContainer.current.innerHTML = '';
      }
    };
  }, []);

  // Handle card tap
  const handleTap = (e) => {
    if (tapTimeoutRef.current) return;

    tapTimeoutRef.current = setTimeout(() => {
      tapTimeoutRef.current = null;
    }, 350);

    const boundingRect = e.currentTarget.getBoundingClientRect();
    const tapX = e.clientX - boundingRect.left;
    const tapDirection = tapX < boundingRect.width / 2 ? 'prev' : 'next';
    console.log("tapDirection",tapDirection, e.currentTarget,currentIndex);
    

    handleNavigation(tapDirection);
  };

  // Handle index navigation
  const handleNavigation = (direction) => {
    if (!newJoinees.length) return;

    setCurrentIndex((prev) => {
      let newIndex;
      if (direction === 'next') {
        newIndex = (prev + 1) % newJoinees.length;
      } else {
        newIndex = (prev - 1 + newJoinees.length) % newJoinees.length;
      }

      // Even if same index (e.g. only one item), restart fade
      if (newIndex === prev) {
        setFadeIn(false);
        setTimeout(() => setFadeIn(true), 10);
      }

      return newIndex;
    });
  };

  // Styled Components
  const StyledCard = styled(Card)(() => ({
    backgroundColor: 'black',
    color: 'white',
    paddingTop: '20vh',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100vh',
    position: 'relative',
    borderRadius: 0,
    border: 1,
    borderColor: 'white',
    overflow: 'hidden',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
    WebkitTouchCallout: 'none',
  }));

  const StyledBox = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    background: 'linear-gradient(45deg, #1679AB, #102C57)',
  }));



  const StyledTypography = styled(Typography)(() => ({
    fontSize: '1.5rem',
    fontWeight: 'bold',
    textAlign: 'center',
    // marginTop: '10px',
    color: 'white',
    textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
  }));

  const CounterTypography = styled(Typography)(() => ({
    position: 'absolute',
    zIndex: 10,
    color: 'white',
    bottom: 90,
    left: '50%',
    transform: 'translateX(-50%)',
    fontWeight: 'bold',
    fontSize: '1.2rem',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    padding: '5px 10px',
    borderRadius: '5px',
    pointerEvents: 'none',
  }));

  // Show loading screen
  if (isLoading) {
    return (
      <Box sx={{ backgroundColor: 'black', width: '100%', height: '100vh', position: 'absolute', zIndex: 100000 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '60vh',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
          <Typography sx={{ mt: 2, color: 'white' }}>Loading...</Typography>
        </Box>
      </Box>
    );
  }

  // Show empty state
  if (isError || newJoinees.length === 0) {
    return (
      <Box sx={{ backgroundColor: 'black', width: '100%', height: '100vh', position: 'absolute', zIndex: 100000 }}>
        <Box
          sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', mt: '10rem' }}
        >
          <img style={{ width: '50%' }} src={empty} alt="No new joinees" />
          <Typography sx={{ fontSize: '1rem', p: 2, color: 'lightgrey' }}>No New Joinees This Month</Typography>
        </Box>
      </Box>
    );
  }

  // Render content
  return (
    <StyledCard onClick={handleTap}>
      <CounterTypography>
        {currentIndex + 1}/{newJoinees.length}
      </CounterTypography>

      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '45%', position: 'relative' }}
      >
        <StyledBox
          sx={{
            opacity: fadeIn ? 1 : 0,
            transition: 'opacity 0.1s ease-in-out',
          }}
        >
          <Avatar
            src={newJoinees[currentIndex].UserImage}
            sx={{
              mt: 1.5,
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              border: '4px solid white',
               img: { objectFit: 'cover', objectPosition: 'top' }
            }}
          />
          <Typography >Welcome Aboard!</Typography>
          <StyledTypography sx={{ color: 'white' }}>
            {newJoinees[currentIndex].FirstName} {newJoinees[currentIndex].LastName}
          </StyledTypography>
          <Typography sx={{ color: 'white' }}>{newJoinees[currentIndex].JoiningDate}</Typography>
          <Typography
            variant="body2"
            sx={{ color: 'white', marginTop: '5px', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}
          >
            Joined Recently 🎉
          </Typography>
        </StyledBox>
      </Box>
    </StyledCard>
  );
};

export default NewsJoinees;
